import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import moment from 'moment';
import NoItemsFound from '../../components/NoItemsFound';
import Pagination from '../../components/CustomPagination';
import SearchInput from '../../components/SearchInput';
import { useNavigate } from 'react-router';
import { useDebounce, useLocalStorage } from '@uidotdev/usehooks';
import Loader from '../../components/Loader';
import {
  GetOrganizationsResponse,
  ManualSubscriptionStatusEnum,
  OrganizationPaymentMethodEnum,
  OwnerWithOrganizationDetails,
} from '@EHSNavigator/backend';
import EyeIcon from '../../assets/icons/eye-close.svg?react';
import { UserWithOrgContext } from '../../types/user';
import { toggleModal } from '../../reducers/modalToggle';
import ModalComponent from '../../components/ModalComponent';
import ExtendSeatModal from '../../components/ExtenedSeatModal';
import AddIcon from '../../assets/icons/add-icon.svg?react';

interface AdminDashboardProps {
  otherClasses?: string;
  data?: GetOrganizationsResponse;
  noDataMessage: string;
  params: {
    search: string;
    page: number;
    limit: number;
  };
  isOrganizationsLoading: boolean;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({
  otherClasses,
  data,
  noDataMessage,
  params,
  isOrganizationsLoading,
}) => {
  const dashboardClasses = clsx(otherClasses, 'flex flex-col w-full flex-1');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useLocalStorage<UserWithOrgContext | null>('userInfo', null);
  const [selectedOwner, setSelectedOwner] = useState<OwnerWithOrganizationDetails | null>(null);

  const modalName = useSelector((state: { modalToggle: { modalName: string } }) => state.modalToggle.modalName);

  const [state, setState] = useState({
    page: params?.page || 1,
    search: params?.search || '',
    limit: params?.limit || 10,
  });

  const debouncedSearch = useDebounce(state?.search, 600);
  const prevFiltersRef = useRef({});
  const isFirstRun = useRef(true);

  useEffect(() => {
    const filters = {
      search: debouncedSearch,
      limit: state.limit,
    };

    let shouldResetPage = false;
    if (!isFirstRun.current) {
      const filtersChanged =
        JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
      if (filtersChanged) {
        shouldResetPage = true;
        setState((prev) => ({ ...prev, page: 1 }));
      }
    }

    const paramsSearch = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'limit' && Number(value) === 10) {
        return;
      }
      if (value && value !== '') {
        paramsSearch.set(key, value.toString());
      }
    });
    if (state.page !== 1 && !shouldResetPage) {
      paramsSearch.set('page', state.page.toString());
    }
    if (shouldResetPage) {
      paramsSearch.delete('page');
      setState((prev) => ({ ...prev, page: 1 }));
    }
    prevFiltersRef.current = filters;
    if (!isFirstRun.current) {
      navigate(`/admin/customers?${paramsSearch.toString()}`);
    }
    isFirstRun.current = false;
  }, [debouncedSearch, state.limit, state.page, navigate]);

  const updateQuery = (queryName: string, queryValue: string) => {
    const params = new URLSearchParams(window.location.search);
    if (queryName === 'page' && queryValue === '1') {
      params.delete('page');
    } else {
      params.set(queryName, queryValue);
    }
    navigate(`/admin/customers?${params.toString()}`);
  };

  const handlePageChange = (newPage: number) => {
    setState((prev) => ({ ...prev, page: newPage }));
    updateQuery('page', String(newPage));
  };

  const handleSetOrgContext = (org: OwnerWithOrganizationDetails) => {
    if (!userInfo) return;

    const updatedUserInfo: UserWithOrgContext = {
      ...userInfo,
      orgContext: org,
    };

    setUserInfo(updatedUserInfo);
    navigate('/user-roles');
  };

  const owners = data?.owners || [];
  const meta = data?.meta;

  const filteredOwners = owners.filter((owner: OwnerWithOrganizationDetails) => {
    return owner.email?.toLowerCase() !== userInfo?.user?.email?.toLowerCase();
  });

  const excludedEmails = [
    'ehsnavigator2025@gmail.com',
    'chris.deblanc@civvywealth.com',
    'bahrozjaved6@gmail.com',
    'official.muhammadawais@gmail.com',
    'sharjeel.anglebrackets@gmail.com',
    'boehmedaniel33@yahoo.com',
    'nutipyj@mailinator.com'
  ];

  return (
    <div className={dashboardClasses} data-testid="admin-dashboard">
      <div className="flex items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div>
          <h1 className="h3">Admin Dashboard</h1>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
            Oversee All Organizations in the EHS Navigator Platform
          </p>
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 text-lg leading-5 font-medium lg:hidden">
        Oversee All Organizations in the EHS Navigator Platform
      </p>
      <hr className="border-neutral-tones-text/50 mt-9 mb-6 lg:hidden" />
      <SearchInput
        name="search"
        onChange={(e) =>
          setState((prev) => ({
            ...prev,
            search: e?.target?.value,
          }))
        }
        placeholder="Enter organization name or email..."
        value={state?.search}
        disabled={false}
        errorMessage=""
        otherClasses={{ container: 'w-full lg:mt-12' }}
      />

      {/* Summary Cards - Commented out */}
      {/* <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-10 border border-neutral-tones-text/10 bg-card-backgrounds/5 p-6 shadow-sm">
          <h3 className="font-inter-variable text-neutral-tones-text/60 -tracking-text-area text-base leading-5 font-medium lg:text-lg lg:leading-6">
            Total Organizations
          </h3>
          <p className="h1 mt-15 text-brand-byzantine">
            {totalOrganizations > 0 && (
              <CountUp start={0} end={totalOrganizations} duration={2} />
            )}
            {totalOrganizations === 0 && 0}
          </p>
        </div>
        <div className="rounded-10 border border-neutral-tones-text/10 bg-card-backgrounds/5 p-6 shadow-sm">
          <h3 className="font-inter-variable text-neutral-tones-text/60 -tracking-text-area text-base leading-5 font-medium lg:text-lg lg:leading-6">
            Active Organizations
          </h3>
          <p className="h1 mt-15 text-brand-papery">
            {activeOrganizations > 0 && (
              <CountUp start={0} end={activeOrganizations} duration={2} />
            )}
            {activeOrganizations === 0 && 0}
          </p>
        </div>
        <div className="rounded-10 border border-neutral-tones-text/10 bg-card-backgrounds/5 p-6 shadow-sm">
          <h3 className="font-inter-variable text-neutral-tones-text/60 -tracking-text-area text-base leading-5 font-medium lg:text-lg lg:leading-6">
            Total Users
          </h3>
          <p className="h1 mt-15 text-brand-fair-orange">
            {totalUsers > 0 && (
              <CountUp start={0} end={totalUsers} duration={2} />
            )}
            {totalUsers === 0 && 0}
          </p>
        </div>
        <div className="rounded-10 border border-neutral-tones-text/10 bg-card-backgrounds/5 p-6 shadow-sm">
          <h3 className="font-inter-variable text-neutral-tones-text/60 -tracking-text-area text-base leading-5 font-medium lg:text-lg lg:leading-6">
            Inactive Organizations
          </h3>
          <p className="h1 mt-15 text-neutral-tones-text/50">
            {inactiveOrganizations > 0 && (
              <CountUp start={0} end={inactiveOrganizations} duration={2} />
            )}
            {inactiveOrganizations === 0 && 0}
          </p>
        </div>
      </div> */}

      {/* Organizations Table */}
      <div className="global-scrollbar-style mt-12 min-h-52 flex-1 overflow-auto px-0.5">
        {isOrganizationsLoading ? (
          <Loader
            variant="compact"
            otherClasses="bg-neutral-tones-background/50 z-20 backdrop-blur-[2px] lg:!w-[calc(100%-310px)] lg:right-0 lg:left-auto !fixed !top-0 left-0 h-dvh"
          />
        ) : filteredOwners.length > 0 ? (
          <div className="w-full min-w-6xl">
            <div className="border-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b pb-6">
              <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                Sr.
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Organization Name
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Email
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                Active <br /> Users
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                License Count
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                Status
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                Amount Paid
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Date Created
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                Action
              </h6>
            </div>
            <div>
              {owners.map((owner: OwnerWithOrganizationDetails, index: number) => {
                const isManualPayment = owner.org?.paymentMethod === OrganizationPaymentMethodEnum.Manual;
                const paymentDetails = isManualPayment ? null : (owner.paymentDetails as any);

                return (
                  <div
                    key={owner.id}
                    className="border-b-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b py-5"
                  >
                    <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-1 flex items-center text-sm leading-4 font-bold lg:text-base lg:leading-5 lg:font-normal">
                      {meta?.page && meta?.limit
                        ? (meta.page - 1) * meta.limit + index + 1
                        : index + 1}
                    </p>
                    <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal capitalize lg:text-base lg:leading-5">
                      {owner.org?.name}
                    </p>
                    <p className="min-w-0 lowercase text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5">
                      <span className="truncate block w-full">
                        {owner.email || 'N/A'}
                      </span>
                    </p>
                    <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-1 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5">
                      {owner.userCount || 0}
                    </p>
                    <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-1 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5">
                      {isManualPayment ? owner.org?.manualSubscription?.licenseCount || 0 : 'Dynamic'}
                    </p>
                    <div className="col-span-1 flex items-center">
                      <span
                        className={clsx(
                          'font-inter-variable -tracking-form-input text-sm leading-4 font-bold uppercase lg:text-base lg:leading-5',
                          (isManualPayment ? owner.org?.manualSubscription?.status === ManualSubscriptionStatusEnum.Active : (paymentDetails?.status as any) === 'active')
                            ? 'text-brand-papery'
                            : 'text-neutral-tones-text/50',
                        )}
                      >
                        {
                          (isManualPayment ? owner.org?.manualSubscription?.status.replace('_', ' ') : paymentDetails?.status) || 'unpaid'
                        }
                      </span>
                    </div>
                    <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-1 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5">
                      {
                        (
                          isManualPayment ? (owner.org?.manualSubscription?.amount ?
                            `$${owner.org?.manualSubscription?.amount}` : 'Not specified') :
                            (owner.totalAmountPaid ? "$" + owner.totalAmountPaid : 'N/A')
                        )
                      }
                    </p>
                    <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5">
                      {owner.org?.createdAt
                        ? moment(owner.org?.createdAt).format('MMMM D, YYYY')
                        : 'N/A'}
                    </p>
                    <div className="col-span-1 flex items-center gap-2.5">
                      <button
                        title="Visit organization"
                        aria-label={`Set context for ${owner.org?.name}`}
                        onClick={() => handleSetOrgContext(owner)}
                        disabled={
                          !excludedEmails.includes(owner.email.toLocaleLowerCase()) &&
                            isManualPayment ?
                            (owner.org?.manualSubscription?.status !== ManualSubscriptionStatusEnum.Active) :
                            (paymentDetails?.status as any) !== 'active'
                        }
                        className="border-neutral-tones-text/10 bg-card-backgrounds/5 text-brand-byzantine disabled:text-neutral-tones-text/50 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border disabled:cursor-not-allowed enabled:hover:scale-105 transition-transform"
                      >
                        <EyeIcon className="h-5 w-5 [&>path]:fill-current" />
                      </button>
                      {isManualPayment && owner.org?.manualSubscription?.status === ManualSubscriptionStatusEnum.Active && (
                        <button
                          title="Extend seats"
                          aria-label={`Extend seats for ${owner.org?.name}`}
                          onClick={() => {
                            setSelectedOwner(owner);
                            dispatch(toggleModal('extend-seat'));
                          }}
                          disabled={excludedEmails.includes(owner.email.toLocaleLowerCase())}
                          className="border-neutral-tones-text/10 bg-card-backgrounds/5 text-brand-byzantine disabled:text-neutral-tones-text/50 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border disabled:cursor-not-allowed enabled:hover:scale-105 transition-transform"
                        >
                          <AddIcon className="h-5 w-5 [&>path]:fill-current" />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <NoItemsFound message={noDataMessage} />
        )}
      </div>
      {filteredOwners.length > 0 && meta && (
        <Pagination
          currentPage={state.page}
          totalPages={meta.totalPages}
          totalItems={meta.total}
          limit={meta.limit}
          showingItems={owners?.length}
          onPageChange={handlePageChange}
          onLimitChange={(e) =>
            setState((prev) => ({ ...prev, limit: e, page: 1 }))
          }
          otherClasses=""
        />
      )}
      {modalName === 'extend-seat' && (
        <ModalComponent
          otherClasses="px-4 sm:px-0"
          additionalClass="w-full max-w-573 rounded-2xl p-5 lg:px-9 lg:py-6"
          customClose={() => {
            setSelectedOwner(null);
            dispatch(toggleModal(''));
          }}
        >
          <ExtendSeatModal
            selectedOwner={selectedOwner}
            handleClose={() => {
              setSelectedOwner(null);
              dispatch(toggleModal(''));
            }}
            queryParams={params}
          />
        </ModalComponent>
      )}
    </div>
  );
};

export default AdminDashboard;

